import { useLingui } from "@lingui/react";
import { useEffect } from "react";

/**
 * Locales that use right-to-left (RTL) text direction.
 * The locale codes here use BCP-47 language tags (language subtag only).
 */
const RTL_LOCALES = new Set(["ar", "he", "fa", "ur"]);

/**
 * Extract the base language code from a BCP-47 locale string.
 * E.g. "ar-SA" -> "ar", "he-IL" -> "he", "en-US" -> "en"
 */
function getLanguageCode(locale: string): string {
  return locale.split("-")[0].toLowerCase();
}

/**
 * `useDirection` — returns the text direction for the active LinguiJS locale.
 *
 * Side effect: sets `document.documentElement.dir` and
 * `document.documentElement.lang` on every locale change so that the entire
 * page layout adapts to RTL/LTR without a full reload.
 *
 * @returns `"rtl"` when the active locale is in RTL_LOCALES, else `"ltr"`.
 *
 * @example
 * ```tsx
 * const dir = useDirection(); // "rtl" when locale is "ar-SA"
 * ```
 */
export function useDirection(): "ltr" | "rtl" {
  const { i18n } = useLingui();
  const locale = i18n.locale;
  const langCode = getLanguageCode(locale);
  const dir: "ltr" | "rtl" = RTL_LOCALES.has(langCode) ? "rtl" : "ltr";

  useEffect(() => {
    document.documentElement.dir = dir;
    document.documentElement.lang = locale;
  }, [dir, locale]);

  return dir;
}
